import type { Sort, Transfer } from "@estelle/domain";

import { reportingNow } from "@/lib/reportClock";

const DAY_MS = 86_400_000;

/**
 * The two statuses where a transfer has stock the destination has not (yet,
 * or fully) received. `draft` never dispatched and `received`/`cancelled`
 * have nothing outstanding, so none of those three ever contribute a row
 * here.
 */
const OUTSTANDING_STATUSES = new Set<Transfer["status"]>([
  "dispatched",
  "partially_received",
]);

/** One line still moving between warehouses, per FR-WHS-12. */
export interface InTransitLine {
  id: string;
  reference: string;
  fromWarehouseName: string;
  toWarehouseName: string;
  /** `quantityDispatched - quantityReceived` for this line. Always > 0. */
  quantity: number;
  dispatchedAt: string;
  ageDays: number;
}

/**
 * FR-WHS-12: list every in-transit line with its transfer, route, dispatch
 * date and age, so a transfer dispatched and never received becomes visible
 * rather than lost.
 *
 * One row per line rather than one per transfer, because a transfer can be
 * `partially_received` with some lines fully in and one line still short —
 * it is that one line that has to surface, not a transfer-level status that
 * would hide it behind the lines that already arrived.
 *
 * `warehouseName` is injected rather than looked up here: `Transfer` only
 * carries `fromWarehouseId` / `toWarehouseId`, and this function has no
 * business reaching past the rows it was handed to resolve them itself —
 * the same restriction `projectOrder` documents for the same reason.
 */
export function deriveInTransitLines(
  transfers: readonly Transfer[],
  warehouseName: (id: string) => string,
): InTransitLine[] {
  const rows: InTransitLine[] = [];

  for (const transfer of transfers) {
    if (!OUTSTANDING_STATUSES.has(transfer.status)) continue;
    // Every dispatched or partially-received transfer has a dispatch date —
    // only draft and cancelled ones don't — but the field is nullable, and a
    // row with no dispatch date has no age to report.
    if (!transfer.dispatchedAt) continue;

    const ageDays = Math.floor(
      (reportingNow() - Date.parse(transfer.dispatchedAt)) / DAY_MS,
    );

    for (const line of transfer.lines) {
      const quantity = line.quantityDispatched - line.quantityReceived;
      // A line inside a partially-received transfer can already be fully
      // received while a sibling line is not — it is the sibling that keeps
      // the transfer open, and this line has nothing left in transit.
      if (quantity <= 0) continue;

      rows.push({
        id: line.id,
        reference: transfer.reference,
        fromWarehouseName: warehouseName(transfer.fromWarehouseId),
        toWarehouseName: warehouseName(transfer.toWarehouseId),
        quantity,
        dispatchedAt: transfer.dispatchedAt,
        ageDays,
      });
    }
  }

  return rows;
}

/**
 * How old is old.
 *
 * A transfer sitting at 20 days is the reason this screen exists — it must
 * not render the same as one dispatched yesterday. Two thresholds rather
 * than one: `aging` is a nudge to check in, `stale` is the state someone
 * should be chasing down.
 */
export type AgeSeverity = "fresh" | "aging" | "stale";

export function ageSeverity(ageDays: number): AgeSeverity {
  if (ageDays >= 14) return "stale";
  if (ageDays >= 7) return "aging";
  return "fresh";
}

function comparable(value: unknown): string | number {
  if (typeof value === "number") return value;
  if (typeof value === "string") return value;
  return "";
}

/**
 * Sort derived lines against the shared `Sort[]` shape the toolbar produces.
 *
 * There is no repository call these rows come from — they are computed by
 * `deriveInTransitLines` above — so there is nothing for the shared list
 * framework's usual "forward `sort` to the repository" pattern to forward
 * to. The screen owns the derivation, so it owns applying the sort to what
 * it derived, the same way it owns the filtering (see `InTransit.tsx`).
 */
export function sortInTransitLines(
  lines: readonly InTransitLine[],
  sort: readonly Sort[],
  project: (line: InTransitLine) => Record<string, unknown>,
): InTransitLine[] {
  if (sort.length === 0) return [...lines];

  return [...lines].sort((a, b) => {
    const pa = project(a);
    const pb = project(b);
    for (const { column, direction } of sort) {
      const left = comparable(pa[column]);
      const right = comparable(pb[column]);
      const result =
        typeof left === "number" && typeof right === "number"
          ? left - right
          : String(left).localeCompare(String(right));
      if (result !== 0) return direction === "asc" ? result : -result;
    }
    return 0;
  });
}
